import React, { useCallback } from 'react';

import Input from '../../../common2/Input';
import DateInput from '../../../common2/DateInput';
import ColumnsLayout from '../../../common/layout/ColumnsLayout';
import Column from '../../../common/layout/Column';
import CarrierSelect from './CarrierSelect';
import Select from '../../../common2/Select';
import { ClaimContext } from '../types';
import ConditionalDeliveryInput from './ConditionalDeliveryInput';
import { recipientCountryOptions } from '../countries';
import { SelectOption } from './useClaimCarrierSelectOptions';

type ClaimContextForFormValues = Pick<
  ClaimContext,
  | 'type'
  | 'trackingCode'
  | 'recipientName'
  | 'recipientAddress'
  | 'recipientCity'
  | 'recipientZipCode'
  | 'recipientCountryCode'
  | 'carrierCode'
  | 'carrierCountryCode'
  | 'firstHubScanDate'
  | 'ssHubScanDate'
  | 'deliveryDate'
  | 'warehouseDeliveryDate'
  | 'postalReturnDeliveryDate'
  | 'isMultiShipmentOrder'
>;

export type EasySubmissionShippingInformationProps = {
  claim: ClaimContextForFormValues;
  onChange: (data: Partial<ClaimContext>) => void;
  carrierOptions?: SelectOption[];
  carrierCountryOptions?: SelectOption[];
  isManual: boolean;
  errors: Record<string, string>;
};

function EasySubmissionShippingInformation(props: EasySubmissionShippingInformationProps) {
  const {
    claim: {
      trackingCode,
      carrierCountryCode,
      recipientName,
      recipientAddress,
      recipientCity,
      recipientZipCode,
      recipientCountryCode,
      carrierCode,
      firstHubScanDate,
      ssHubScanDate,
      deliveryDate,
      warehouseDeliveryDate,
      postalReturnDeliveryDate,
      isMultiShipmentOrder,
    },
    carrierOptions = [],
    carrierCountryOptions = [],
    onChange,
    isManual,
    errors,
  } = props;

  const onInputChange = useCallback(
    (value: unknown, name: string) => {
      onChange({ [name]: value });
    },
    [onChange]
  );

  const showFirstHubScan = !!firstHubScanDate || !!ssHubScanDate;

  // carrier and dates are disabled because they are filled in a previous step, but still shown as inputs here
  return (
    <Column spacing="medium">
      <ColumnsLayout>
        <Input
          name="trackingCode"
          label="SHIPPING_INFO_TRACKING_ID"
          placeholder="Add tracking ID"
          isRequired
          value={trackingCode}
          disabled
          onChange={onInputChange}
        />
        <Select
          name="isMultiShipmentOrder"
          label="Order type"
          placeholder="Select order type"
          options={[
            { value: true, label: 'Multi shipment order' },
            { value: false, label: 'Single shipment order' },
          ]}
          isRequired
          value={isMultiShipmentOrder}
          isDisabled={!isManual}
          onChange={onInputChange}
          noErrorTranslation
          error={errors.isMultiShipmentOrder}
        />
        <CarrierSelect
          isDisabled
          carrierCode={carrierCode}
          carrierCountryCode={carrierCountryCode}
          carrierOptions={carrierOptions}
          carrierCountryOptions={carrierCountryOptions}
          onChange={onChange}
        />
        {showFirstHubScan ? (
          <DateInput
            name="firstHubScanDate"
            label="First hub scan"
            placeholder="Add date"
            isRequired
            value={firstHubScanDate || ssHubScanDate}
            disabled
            onChange={onInputChange}
          />
        ) : (
          <div />
        )}
        <ConditionalDeliveryInput
          deliveryDate={deliveryDate}
          warehouseDeliveryDate={warehouseDeliveryDate}
          postalReturnDeliveryDate={postalReturnDeliveryDate}
        />
      </ColumnsLayout>
      <ColumnsLayout>
        <Input
          name="recipientName"
          label="Recipient name"
          placeholder="Add name"
          isRequired
          value={recipientName || ''}
          onChange={onInputChange}
          noErrorTranslation
          disabled={!isManual}
          error={errors.recipientName}
        />
        <Input
          name="recipientAddress"
          label="Recipient address"
          placeholder="Add address"
          isRequired
          value={recipientAddress || ''}
          onChange={onInputChange}
          noErrorTranslation
          disabled={!isManual}
          error={errors.recipientAddress}
        />
        <Input
          name="recipientZipCode"
          label="Recipient zip code"
          placeholder="Add zip code"
          isRequired
          value={recipientZipCode || ''}
          onChange={onInputChange}
          noErrorTranslation
          disabled={!isManual}
          error={errors.recipientZipCode}
        />
        <Input
          name="recipientCity"
          label="Recipient city"
          placeholder="Add city"
          isRequired
          value={recipientCity || ''}
          onChange={onInputChange}
          noErrorTranslation
          disabled={!isManual}
          error={errors.recipientCity}
        />
        <Select
          name="recipientCountryCode"
          label="Recipient country"
          placeholder="Select country"
          options={recipientCountryOptions}
          value={recipientCountryCode}
          onChange={onInputChange}
          noErrorTranslation
          isDisabled={!isManual}
          isRequired
          isSearchable
          menuPlacement="auto"
          useMenuPortal
          error={errors.recipientCountryCode}
        />
      </ColumnsLayout>
    </Column>
  );
}

export default React.memo(EasySubmissionShippingInformation);
